// Phase 9B — useBoardItems hook.
//
// Read-only consumer of board_items table. Fetches all board items
// for the authenticated user, scoped to a specific board_type.
// Supports status filtering, priority sorting, and activity log reads.
//
// Trust contract:
//   - Read via RLS-scoped SELECT. Writes via RPCs only.
//   - Defensive parsing — missing fields render with fallbacks.
//   - No localStorage. No external HTTP.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ─────────────────────────────────────────────────────

export type BoardType =
  | "opportunities"
  | "chains"
  | "risk"
  | "results"
  | "goals"
  | "admin";

export type BoardItemStatus =
  | "detected"
  | "qualifying"
  | "backlog"
  | "queued"
  | "active"
  | "in_review"
  | "approved"
  | "ready"
  | "executing"
  | "live"
  | "monitoring"
  | "suspended"
  | "resolved_win"
  | "resolved_loss"
  | "resolved_void"
  | "archived"
  | "learned";

export type BoardPriority = "critical" | "high" | "medium" | "low" | "none";

export interface BoardItem {
  id: string;
  board_type: BoardType;
  title: string;
  subtitle: string | null;
  description: string | null;
  status: BoardItemStatus;
  priority: BoardPriority;
  market_type: string;
  market_subtype: string | null;
  signal_type: string | null;
  signal_id: string | null;
  cycle_id: string | null;
  fixture_id: string | null;
  value_primary: number | null;
  value_secondary: number | null;
  value_pct: number | null;
  confidence_score: number | null;
  edge_value: number | null;
  risk_score: number | null;
  signal_source: string | null;
  agent_id: string | null;
  chain_id: string | null;
  execution_notes: string | null;
  outcome_notes: string | null;
  learning_notes: string | null;
  tags: readonly string[];
  meta: Record<string, unknown>;
  requires_approval: boolean;
  approved_at: string | null;
  approved_by: string | null;
  due_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  position: number;
  simulated: boolean;
  created_at: string;
  updated_at: string;
}

export interface ActivityLogEntry {
  id: string;
  board_type: BoardType | null;
  board_item_id: string | null;
  action: string;
  actor: string;
  summary: string | null;
  event_type: string | null;
  severity: string;
  market_type: string | null;
  signal_type: string | null;
  agent_id: string | null;
  chain_id: string | null;
  confidence: number | null;
  duration_ms: number | null;
  tags: readonly string[];
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  created_at: string;
}

export interface ApprovalWorkflow {
  id: string;
  board_item_id: string;
  requested_action: string;
  requested_at: string;
  decision: string | null;
  decided_at: string | null;
  reason: string | null;
  meta: Record<string, unknown>;
  created_at: string;
}

export interface UseBoardItemsState {
  items: readonly BoardItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Row parser ────────────────────────────────────────────────

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function asNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseBoardItem(row: Record<string, unknown>): BoardItem {
  return {
    id: String(row.id ?? ""),
    board_type: (row.board_type as BoardType) ?? "opportunities",
    title: String(row.title ?? ""),
    subtitle: asStr(row.subtitle),
    description: asStr(row.description),
    status: (row.status as BoardItemStatus) ?? "backlog",
    priority: (row.priority as BoardPriority) ?? "none",
    market_type: String(row.market_type ?? "football"),
    market_subtype: asStr(row.market_subtype),
    signal_type: asStr(row.signal_type),
    signal_id: asStr(row.signal_id),
    cycle_id: asStr(row.cycle_id),
    fixture_id: asStr(row.fixture_id),
    value_primary: asNum(row.value_primary),
    value_secondary: asNum(row.value_secondary),
    value_pct: asNum(row.value_pct),
    confidence_score: asNum(row.confidence_score),
    edge_value: asNum(row.edge_value),
    risk_score: asNum(row.risk_score),
    signal_source: asStr(row.signal_source),
    agent_id: asStr(row.agent_id),
    chain_id: asStr(row.chain_id),
    execution_notes: asStr(row.execution_notes),
    outcome_notes: asStr(row.outcome_notes),
    learning_notes: asStr(row.learning_notes),
    tags: Array.isArray(row.tags) ? (row.tags as string[]) : [],
    meta: typeof row.meta === "object" && row.meta !== null
      ? (row.meta as Record<string, unknown>)
      : {},
    requires_approval: row.requires_approval === true,
    approved_at: asStr(row.approved_at),
    approved_by: asStr(row.approved_by),
    due_at: asStr(row.due_at),
    started_at: asStr(row.started_at),
    completed_at: asStr(row.completed_at),
    position: typeof row.position === "number" ? row.position : 0,
    simulated: true,
    created_at: String(row.created_at ?? ""),
    updated_at: String(row.updated_at ?? ""),
  };
}

// ─── Priority sort weight ──────────────────────────────────────

const PRIORITY_WEIGHT: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  none: 4,
};

function sortByPriorityThenPosition(a: BoardItem, b: BoardItem): number {
  const pa = PRIORITY_WEIGHT[a.priority] ?? 4;
  const pb = PRIORITY_WEIGHT[b.priority] ?? 4;
  if (pa !== pb) return pa - pb;
  return a.position - b.position;
}

// ─── Hook ──────────────────────────────────────────────────────

export function useBoardItems(
  boardType: BoardType,
  opts: {
    statusFilter?: BoardItemStatus;
  } = {},
): UseBoardItemsState {
  const { user } = useAuthSession();
  const [items, setItems] = useState<readonly BoardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setItems([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      // Build query — cast through unknown since generated types are stale.
      let query = (
        client.from("board_items" as never) as unknown as {
          select: (cols: string) => {
            eq: (col: string, val: string) => {
              eq: (col: string, val: string) => {
                order: (col: string, opts: { ascending: boolean }) => Promise<{
                  data: Record<string, unknown>[] | null;
                  error: { message: string } | null;
                }>;
              };
              order: (col: string, opts: { ascending: boolean }) => Promise<{
                data: Record<string, unknown>[] | null;
                error: { message: string } | null;
              }>;
            };
          };
        }
      )
        .select("*")
        .eq("board_type", boardType);

      let resp: {
        data: Record<string, unknown>[] | null;
        error: { message: string } | null;
      };

      if (opts.statusFilter) {
        resp = await query
          .eq("status", opts.statusFilter)
          .order("created_at", { ascending: false });
      } else {
        resp = await query.order("created_at", { ascending: false });
      }

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setItems([]);
        setLoading(false);
        return;
      }

      const parsed = (resp.data ?? [])
        .filter((r) => r !== null)
        .map(parseBoardItem)
        .sort(sortByPriorityThenPosition);

      setItems(parsed);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, boardType, opts.statusFilter, tick]);

  return useMemo(
    () => ({ items, loading, error, refetch }),
    [items, loading, error, refetch],
  );
}

// ─── Activity log hook ─────────────────────────────────────────

export interface UseActivityLogState {
  entries: readonly ActivityLogEntry[];
  loading: boolean;
  error: string | null;
}

function parseActivityLog(row: Record<string, unknown>): ActivityLogEntry {
  return {
    id: String(row.id ?? ""),
    board_type: asStr(row.board_type) as BoardType | null,
    board_item_id: asStr(row.board_item_id),
    action: String(row.action ?? ""),
    actor: String(row.actor ?? "user"),
    summary: asStr(row.summary),
    event_type: asStr(row.event_type),
    severity: String(row.severity ?? "info"),
    market_type: asStr(row.market_type),
    signal_type: asStr(row.signal_type),
    agent_id: asStr(row.agent_id),
    chain_id: asStr(row.chain_id),
    confidence: asNum(row.confidence),
    duration_ms: asNum(row.duration_ms),
    tags: Array.isArray(row.tags) ? (row.tags as string[]) : [],
    old_value: typeof row.old_value === "object" && row.old_value !== null
      ? (row.old_value as Record<string, unknown>)
      : null,
    new_value: typeof row.new_value === "object" && row.new_value !== null
      ? (row.new_value as Record<string, unknown>)
      : null,
    created_at: String(row.created_at ?? ""),
  };
}

export function useActivityLog(
  boardType?: BoardType,
  limit = 50,
): UseActivityLogState {
  const { user } = useAuthSession();
  const [entries, setEntries] = useState<readonly ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setEntries([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      let resp: {
        data: Record<string, unknown>[] | null;
        error: { message: string } | null;
      };

      if (boardType) {
        resp = await (
          client.from("activity_log" as never) as unknown as {
            select: (cols: string) => {
              eq: (col: string, val: string) => {
                order: (col: string, opts: { ascending: boolean }) => {
                  limit: (n: number) => Promise<{
                    data: Record<string, unknown>[] | null;
                    error: { message: string } | null;
                  }>;
                };
              };
            };
          }
        )
          .select("*")
          .eq("board_type", boardType)
          .order("created_at", { ascending: false })
          .limit(limit);
      } else {
        resp = await (
          client.from("activity_log" as never) as unknown as {
            select: (cols: string) => {
              order: (col: string, opts: { ascending: boolean }) => {
                limit: (n: number) => Promise<{
                  data: Record<string, unknown>[] | null;
                  error: { message: string } | null;
                }>;
              };
            };
          }
        )
          .select("*")
          .order("created_at", { ascending: false })
          .limit(limit);
      }

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setEntries([]);
        setLoading(false);
        return;
      }

      setEntries((resp.data ?? []).filter((r) => r !== null).map(parseActivityLog));
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, boardType, limit]);

  return useMemo(() => ({ entries, loading, error }), [entries, loading, error]);
}

// ─── Pending approvals hook ────────────────────────────────────

export interface UsePendingApprovalsState {
  approvals: readonly ApprovalWorkflow[];
  loading: boolean;
  error: string | null;
}

function parseApproval(row: Record<string, unknown>): ApprovalWorkflow {
  return {
    id: String(row.id ?? ""),
    board_item_id: String(row.board_item_id ?? ""),
    requested_action: String(row.requested_action ?? ""),
    requested_at: String(row.requested_at ?? ""),
    decision: asStr(row.decision),
    decided_at: asStr(row.decided_at),
    reason: asStr(row.reason),
    meta: typeof row.meta === "object" && row.meta !== null
      ? (row.meta as Record<string, unknown>)
      : {},
    created_at: String(row.created_at ?? ""),
  };
}

export function usePendingApprovals(): UsePendingApprovalsState {
  const { user } = useAuthSession();
  const [approvals, setApprovals] = useState<readonly ApprovalWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setApprovals([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      const resp = await (
        client.from("approval_workflows" as never) as unknown as {
          select: (cols: string) => {
            is: (col: string, val: null) => {
              order: (col: string, opts: { ascending: boolean }) => Promise<{
                data: Record<string, unknown>[] | null;
                error: { message: string } | null;
              }>;
            };
          };
        }
      )
        .select("*")
        .is("decision", null)
        .order("created_at", { ascending: false });

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setApprovals([]);
        setLoading(false);
        return;
      }

      setApprovals(
        (resp.data ?? []).filter((r) => r !== null).map(parseApproval),
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  return useMemo(
    () => ({ approvals, loading, error }),
    [approvals, loading, error],
  );
}
